/**
 * Shared telemetry chart definitions (same metric set as Dashboard scenario panels, excluding bots IP table).
 */
import type { ComponentType } from 'react';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import {
  Speed as SpeedIcon,
  NetworkCheck as NetworkIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import type { TimeseriesSeries } from '../components/metrics/TimeseriesCard';
import type { MetricsData } from '../hooks/useGrafanaMetrics';

export type TelemetryChartPanelId =
  | 'bandwidth'
  | 'packets'
  | 'botsByProvider'
  | 'tcp'
  | 'rps'
  | 'successRate'
  | 'responseTime';

export const TELEMETRY_CHART_PANEL_IDS: TelemetryChartPanelId[] = [
  'bandwidth',
  'packets',
  'botsByProvider',
  'tcp',
  'rps',
  'successRate',
  'responseTime',
];

export const TELEMETRY_PANEL_LABELS: Record<TelemetryChartPanelId, string> = {
  bandwidth: 'Bandwidth in/out',
  packets: 'Packets per second',
  botsByProvider: 'Bots by provider',
  tcp: 'Concurrent TCP connections',
  rps: 'Requests per second',
  successRate: 'Success rate',
  responseTime: 'Response time',
};

/**
 * Attack classes whose bots report socket operations -- held TCP connections, DNS
 * queries, raw HTTP/2 frames -- rather than HTTP requests. Those still feed the
 * `http_client_request_*` counters behind the RPS card, so the card is relabelled
 * rather than hidden: Carpet Bombing and Combined Volumetric scenarios mix classes,
 * and a class-based filter would hide panels that do carry real data.
 */
export function isSocketOperationAttackClass(attackClass?: string | null): boolean {
  return attackClass === 'infrastructure' || attackClass === 'volumetric';
}

/** localStorage key for Executions page metric picker. */
export const EXECUTION_TELEMETRY_PANELS_STORAGE_KEY = 'executions.telemetry.visiblePanels.v1';

/**
 * Whether the "Visible metrics" multi-select section is expanded (Executions embedded metrics).
 * Default when unset: collapsed (`false`); persisted as JSON boolean.
 */
export const EXECUTION_VISIBLE_METRICS_PICKER_EXPANDED_KEY =
  'executions.telemetry.visibleMetricsPickerExpanded.v1';

/** Default charts shown on Executions (matches prior MetricsPanel). */
export const DEFAULT_EXECUTION_TELEMETRY_PANELS: TelemetryChartPanelId[] = [
  'bandwidth',
  'packets',
  'tcp',
  'rps',
];

export const TELEMETRY_PANEL_DISPLAY_ORDER: TelemetryChartPanelId[] = [
  'bandwidth',
  'packets',
  'botsByProvider',
  'tcp',
  'rps',
  'successRate',
  'responseTime',
];

export function sortTelemetryPanelIds(ids: readonly string[]): TelemetryChartPanelId[] {
  const valid = new Set<string>(TELEMETRY_CHART_PANEL_IDS);
  const filtered = ids.filter((id): id is TelemetryChartPanelId => valid.has(id));
  return TELEMETRY_PANEL_DISPLAY_ORDER.filter((id) => filtered.includes(id));
}

export function parseStoredTelemetryPanelIds(raw: string | null): TelemetryChartPanelId[] {
  try {
    const p = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(p)) return [...DEFAULT_EXECUTION_TELEMETRY_PANELS];
    const sorted = sortTelemetryPanelIds(p.map(String));
    return sorted.length > 0 ? sorted : [...DEFAULT_EXECUTION_TELEMETRY_PANELS];
  } catch {
    return [...DEFAULT_EXECUTION_TELEMETRY_PANELS];
  }
}

export function formatSiTelemetry(raw: number, unit: string, absolute = false): string {
  if (!Number.isFinite(raw)) return '—';
  const value = Math.abs(raw);
  const sign = absolute ? '' : raw < 0 ? '-' : '';
  const thresholds: Array<[number, string]> = [
    [1e12, 'T'],
    [1e9, 'G'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  for (const [t, prefix] of thresholds) {
    if (value >= t) {
      return `${sign}${(value / t).toFixed(2)} ${prefix}${unit}`;
    }
  }
  return `${sign}${value.toFixed(2)} ${unit}`;
}

export function formatMaybeDecimalTelemetry(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '0';
  const abs = Math.abs(value);
  if (abs > 0 && abs < 1) {
    return value.toFixed(digits);
  }
  return value.toFixed(0);
}

export function buildProviderSeriesFromBotsByProvider(
  botsByProvider: MetricsData['botsByProvider'],
  theme: Theme
): TimeseriesSeries[] {
  const palette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    theme.palette.primary.light,
  ];
  const entries = Object.entries(botsByProvider || {}).sort(([a], [b]) => a.localeCompare(b));
  const hasKnown = entries.some(([provider]) => provider.toLowerCase() !== 'unknown');
  const filtered = hasKnown ? entries.filter(([provider]) => provider.toLowerCase() !== 'unknown') : entries;
  if (filtered.length === 0) {
    return [
      {
        id: 'bots_total',
        label: 'Bots',
        color: alpha(theme.palette.primary.main, 0.75),
        data: [],
        type: 'line' as const,
        strokeWidth: 3,
      },
    ];
  }
  return filtered.map(([provider, data], idx) => ({
    id: provider.replace(/[^a-zA-Z0-9_]/g, '_'),
    label: provider,
    color: palette[idx % palette.length],
    data,
  }));
}

export function computeBotsByProviderYDomain(
  providerSeries: TimeseriesSeries[],
  xDomain: [number, number]
): [number, number] | undefined {
  const [start, end] = xDomain;
  const hasAnyBotsInWindow = providerSeries.some((s) =>
    s.data.some((p) => p.timestamp >= start && p.timestamp <= end && p.value > 0)
  );
  return hasAnyBotsInWindow ? undefined : [0, 1];
}

export interface TelemetryTimeseriesPanelConfig {
  id: TelemetryChartPanelId;
  title: string;
  icon: ComponentType<SvgIconProps>;
  height: number;
  series: TimeseriesSeries[];
  yTickFormatter: (v: number) => string;
  valueFormatter: (v: number) => string;
  yAxisAllowDecimals?: boolean;
  yDomain?: [number, number];
  yAxisPadding?: { bottom: number };
  unit?: string;
}

export function getTelemetryTimeseriesPanelConfig(
  panelId: TelemetryChartPanelId,
  args: {
    metrics: MetricsData;
    theme: Theme;
    providerSeries: TimeseriesSeries[];
    botsByProviderYDomain?: [number, number];
    botsByProviderYAxisPadding?: { bottom: number };
    chartHeight?: number;
    /** Attack class of the scenario in view; relabels the RPS card for socket attacks. */
    attackClass?: string | null;
  }
): TelemetryTimeseriesPanelConfig | null {
  const {
    metrics,
    theme,
    providerSeries,
    botsByProviderYDomain,
    botsByProviderYAxisPadding,
    chartHeight = 200,
    attackClass,
  } = args;
  const h = chartHeight;

  switch (panelId) {
    case 'bandwidth':
      return {
        id: panelId,
        title: TELEMETRY_PANEL_LABELS.bandwidth,
        icon: NetworkIcon,
        height: h,
        series: [
          {
            id: 'down',
            label: 'In',
            color: theme.palette.primary.main,
            data: metrics.bandwidthDown,
            type: 'area' as const,
          },
          {
            id: 'up',
            label: 'Out',
            color: theme.palette.secondary.main,
            data: metrics.bandwidthUp,
            type: 'area' as const,
          },
        ],
        yTickFormatter: (v: number) => formatSiTelemetry(v, 'bps', true),
        valueFormatter: (v: number) => formatSiTelemetry(v, 'bps', true),
      };
    case 'packets':
      return {
        id: panelId,
        title: TELEMETRY_PANEL_LABELS.packets,
        icon: SpeedIcon,
        height: h,
        series: [
          { id: 'up', label: 'Up', color: theme.palette.success.main, data: metrics.packetsUp },
          { id: 'down', label: 'Down', color: theme.palette.warning.main, data: metrics.packetsDown },
        ],
        yTickFormatter: (v: number) => formatSiTelemetry(v, 'pps', true),
        valueFormatter: (v: number) => formatSiTelemetry(v, 'pps', true),
      };
    case 'botsByProvider':
      return {
        id: panelId,
        title: TELEMETRY_PANEL_LABELS.botsByProvider,
        icon: ComputerIcon,
        height: h,
        series: providerSeries,
        yTickFormatter: (v: number) => `${Math.round(v)}`,
        valueFormatter: (v: number) => `${Math.round(v)}`,
        yAxisAllowDecimals: false,
        yDomain: botsByProviderYDomain,
        yAxisPadding: botsByProviderYAxisPadding,
      };
    case 'tcp':
      return {
        id: panelId,
        title: TELEMETRY_PANEL_LABELS.tcp,
        icon: NetworkIcon,
        height: h,
        series: [
          {
            id: 'connections',
            label: 'Connections',
            color: theme.palette.info.main,
            data: metrics.tcpConnections,
          },
          {
            id: 'closed',
            label: 'Closed',
            color: theme.palette.error.main,
            data: metrics.tcpConnectionsClosed,
          },
        ],
        yTickFormatter: (v: number) => `${Math.round(v)}`,
        valueFormatter: (v: number) => `${Math.round(v)}`,
        yAxisAllowDecimals: false,
      };
    case 'rps':
      return {
        id: panelId,
        title: isSocketOperationAttackClass(attackClass)
          ? 'Operations per second'
          : TELEMETRY_PANEL_LABELS.rps,
        icon: SpeedIcon,
        height: h,
        series: [
          { id: 'ok', label: 'OK', color: theme.palette.success.main, data: metrics.rpsOk },
          { id: 'error', label: 'ERROR', color: theme.palette.error.main, data: metrics.rpsError },
          { id: 'total', label: 'TOTAL', color: theme.palette.primary.main, data: metrics.rpsTotal },
        ],
        yTickFormatter: (v: number) => formatMaybeDecimalTelemetry(v),
        valueFormatter: (v: number) => formatMaybeDecimalTelemetry(v),
      };
    case 'successRate':
      return {
        id: panelId,
        title: TELEMETRY_PANEL_LABELS.successRate,
        icon: SpeedIcon,
        height: h,
        unit: '%',
        series: [
          {
            id: 'success',
            label: 'Success',
            color: theme.palette.success.main,
            data: metrics.successRate,
            type: 'area' as const,
          },
        ],
        yTickFormatter: (v: number) => `${v.toFixed(0)}%`,
        valueFormatter: (v: number) => `${v.toFixed(1)}`,
        yDomain: [0, 100] as [number, number],
      };
    case 'responseTime':
      return {
        id: panelId,
        title: TELEMETRY_PANEL_LABELS.responseTime,
        icon: SpeedIcon,
        height: h,
        unit: 's',
        series: [
          {
            id: 'rt',
            label: 'Avg',
            color: theme.palette.info.main,
            data: metrics.responseTime,
            type: 'area' as const,
          },
        ],
        yTickFormatter: (v: number) => `${v.toFixed(2)}s`,
        valueFormatter: (v: number) => v.toFixed(3),
      };
    default:
      return null;
  }
}
